import React, { createContext, useReducer, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { IWorkplaceDetailedDTO } from '@common-frontend-submodule/interfaces/workplace/iworkplace-detailed.interface';
import { IWorkplaceDTO } from '@common-frontend-submodule/interfaces/workplace/iworkplace.interface';
import { IGetWorkplacesRequestDTO } from '@common-frontend-submodule/interfaces/workplace/iget-workplaces-request.interface';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IWorkplaceCreationDTO } from '@common-frontend-submodule/interfaces/workplace/iworkplace-creation.interface';
import { IWorkplaceUpdateDTO } from '@common-frontend-submodule/interfaces/workplace/iworkplace-update.interface';
import { WorkplaceRoutes } from '@common-frontend-submodule/enums/routes/workplace-routes.enum';
import { LocalStorage } from '../../plugins/localStorage';

const apiClient = ApiClient.getInstance()

// Определение типов для состояния
interface WorkplaceState {
  availableWorkplaces: IWorkplaceDetailedDTO[];
  currentWorkplace: IWorkplaceDetailedDTO | null;
}

const loadStateFromLocalStorage = (): WorkplaceState => {
  const availableWorkplaces = LocalStorage.get('workplaces', []);
  const currentWorkplace = LocalStorage.get('current-workplace', null);
  return {
    availableWorkplaces: availableWorkplaces || [],
    currentWorkplace: currentWorkplace || null,
  };
};

// Начальное состояние с учетом LocalStorage
const initialState: WorkplaceState = loadStateFromLocalStorage();

// Определение типов действий
type WorkplaceAction =
  | { type: 'SET_CURRENT_WORKPLACE'; payload: IWorkplaceDetailedDTO }
  | { type: 'SET_AVAILABLE_WORKPLACES'; payload: IWorkplaceDetailedDTO[] };

// Редьюсер для управления состоянием
function workplaceReducer(state: WorkplaceState, action: WorkplaceAction): WorkplaceState {
  switch (action.type) {
    case 'SET_CURRENT_WORKPLACE':
      LocalStorage.set('current-workplace', action.payload);
      return { ...state, currentWorkplace: action.payload };
    case 'SET_AVAILABLE_WORKPLACES':
      LocalStorage.set('workplaces', action.payload);
      return { ...state, availableWorkplaces: action.payload };
    default:
      return state;
  }
}

interface WorkplaceProps extends WorkplaceState {
    setCurrentWorkplace: (workplace: IWorkplaceDetailedDTO) => void;
    setAvailableWorkplaces: (workplaces: IWorkplaceDetailedDTO[]) => void;
    requestAvailable: () => Promise<IApiResponseGenericDTO<IWorkplaceDTO[]>>;
    requestAvailableWithModules: () => Promise<IApiResponseGenericDTO<IWorkplaceDetailedDTO[]>>;
    getById: (workplaceId: string) => Promise<IApiResponseGenericDTO<IWorkplaceDTO>>;
    filter: (model: IGetWorkplacesRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IWorkplaceDTO>>>;
    create: (model: IWorkplaceCreationDTO) => Promise<IApiResponseGenericDTO<IWorkplaceDTO>>;
    update: (workplaceId: string, model: IWorkplaceUpdateDTO) => Promise<IApiResponseGenericDTO<IWorkplaceDTO>>;
    delete: (workplaceId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const WorkplaceContext = createContext<WorkplaceProps | undefined>(undefined);

// Провайдер контекста
export const WorkplaceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(workplaceReducer, initialState);
  const {availableWorkplaces, currentWorkplace} = state

  // Действия для управления состоянием
  const setCurrentWorkplace = (workplace: IWorkplaceDetailedDTO) => {
    dispatch({ type: 'SET_CURRENT_WORKPLACE', payload: workplace });
  };

  const setAvailableWorkplaces = (workplaces: IWorkplaceDetailedDTO[]) => {
    dispatch({ type: 'SET_AVAILABLE_WORKPLACES', payload: workplaces });
  };

  const requestAvailable = async (): Promise<IApiResponseGenericDTO<IWorkplaceDTO[]>> => {
    return await apiClient.getRequest<IWorkplaceDTO[]>(`${WorkplaceRoutes.BasePrefix}/${WorkplaceRoutes.Available}`);
  };

  const requestAvailableWithModules = async (): Promise<IApiResponseGenericDTO<IWorkplaceDetailedDTO[]>> => {
    const result = await apiClient.getRequest<IWorkplaceDetailedDTO[]>(`${WorkplaceRoutes.BasePrefix}/${WorkplaceRoutes.AvailableWithModules}`);
    if (result.success) {
      setAvailableWorkplaces(result.data);
      const currentWorkplace = state.currentWorkplace;
      if (!currentWorkplace || !result.data.find((i) => i.id === currentWorkplace.id)) {
        setCurrentWorkplace(result.data[0] ?? ({} as IWorkplaceDetailedDTO));
      } else {
        const updatedWorkplace = result.data.find((i) => i.id === currentWorkplace.id) as IWorkplaceDetailedDTO;
        setCurrentWorkplace(updatedWorkplace);
      }
    }
    return result;
  };

  const getById = async (workplaceId: string): Promise<IApiResponseGenericDTO<IWorkplaceDTO>> => {
    return await apiClient.getRequest<IWorkplaceDTO>(`${WorkplaceRoutes.BasePrefix}/${workplaceId}`);
  };

  const filter = async (model: IGetWorkplacesRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IWorkplaceDTO>>> => {
    return await apiClient.postRequest<IGetWorkplacesRequestDTO, IPaginatedResultDTO<IWorkplaceDTO>>(`${WorkplaceRoutes.BasePrefix}/${WorkplaceRoutes.List}`, model);
  };

  const create = async (model: IWorkplaceCreationDTO): Promise<IApiResponseGenericDTO<IWorkplaceDTO>> => {
    return await apiClient.postRequest<IWorkplaceCreationDTO, IWorkplaceDTO>(WorkplaceRoutes.BasePrefix, model);
  };

  const update = async (workplaceId: string, model: IWorkplaceUpdateDTO): Promise<IApiResponseGenericDTO<IWorkplaceDTO>> => {
    return await apiClient.putRequest<IWorkplaceUpdateDTO, IWorkplaceDTO>(`${WorkplaceRoutes.BasePrefix}/${workplaceId}`, model);
  };

  const deleteWorkplace = async (workplaceId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${WorkplaceRoutes.BasePrefix}/${workplaceId}`);
  };

  return (
    <WorkplaceContext.Provider
      value={{
          availableWorkplaces,
          currentWorkplace,
          setCurrentWorkplace,
          setAvailableWorkplaces,
          requestAvailable,
          requestAvailableWithModules,
          getById,
          filter,
          create,
          update,
          delete: deleteWorkplace,
      }}
    >
      {children}
    </WorkplaceContext.Provider>
  );
};

// Хук для использования контекста
export const useWorkplaceStore = () => {
  const context = useContext(WorkplaceContext);
  if (!context) {
    throw new Error('useWorkplace must be used within a WorkplaceProvider');
  }
  return context;
};
